<template>
	<v-container style="background-color: #ffffff">
		<p>
			<i>Please map your value chain to get an overview of key risks and opportunities, improve transparency, strengthen partnerships, and support sustainable decision-making.</i>
		</p>
		<div class="pb-6 text-right">
			<v-btn
				color="orangeButton"
				class="white text-h7"
				target="_blank"
			>
			<router-link style="text-decoration: none; color: white"
				:to="{
					path: '/knowledge',
					query: {
						article: 'Sustainability Tutorial',
						doc: 'value-chain'
					}
				}"
				target="_blank"> Value Chain Guide <v-icon class="pl-2">fas fa-external-link-alt</v-icon>
			</router-link>
			</v-btn>
		</div>
		<v-card class="pa-6 mb-6" elevation="0" outlined>
			<div class="value-chain-container">
				<div class="section-label">SUPPLIERS</div>

				<draggable
					v-model="allTiers"
					class="chain-boxes"
					@end="onDragEnd"
					:options="{ animation: 200 }"
				>
					<v-card 
						v-for="(tier, index) in allTiers"
						:key="'tier-' + index"
						:class="['tier-box', { 'company-box': tier.type === 'company' }]"
						:color="tier.type === 'company' ? '#2d9b9b' : ''"
						outlined
					>
						<!-- Company Box -->
						<template v-if="tier.type === 'company'">
							<div class="company-title">The company</div>
							<v-textarea
								v-model="company.product"
								placeholder="Product/ Service the company deliver"
								dark dense outlined rows="4"
								class="mb-2 square-textarea"
							></v-textarea>
							<v-textarea
								v-model="company.businessModel"
								placeholder="Business model"
								dark dense outlined rows="2"
								class="square-textarea"
							></v-textarea>
						</template>

						<!-- Tier Box -->
						<template v-else>
							<div class="tier-title">{{ tier.displayName }}</div>
							<div v-for="(item, itemIndex) in tier.items" :key="'item-' + index + '-' + itemIndex" class="mb-2" style="position: relative;">
								<v-textarea
									v-model="tier.items[itemIndex]"
									outlined dense rows="2"
									:placeholder="tier.placeholder"
								></v-textarea>
								<v-btn 
									v-if="tier.items.length > 1"
									icon x-small 
									color="error" 
									@click="removeItemFromTier(index, itemIndex)"
									style="position: absolute; top: -8px; right: -8px; z-index: 1;"
								>
									<v-icon small>fas fa-thin fa-times</v-icon>
								</v-btn>
							</div>
							<v-btn small text color="primary" @click="addItemToTier(index)">
								+ Add Item
							</v-btn>
						</template>
					</v-card>
				</draggable>
				<div class="section-label-right">CUSTOMERS</div>
			</div>

			<p class="text-right mb-10" style="font-size: 14px">
				<v-icon small>fas fa-arrows-alt</v-icon> 
				<i>You can customize the number of suppliers and customers in your value chain and rearrange tiers by dragging the blocks left or right to reflect your company’s structure.</i>
			</p>
			<v-card class="info-section mb-3" outlined>
				<v-row no-gutters>
					<v-col cols="6" class="pa-4 border-right">
						<div class="section-heading">Raw materials
							<p style="font-size: 13px; font-weight: normal;"><i>List all raw materials used in your production.</i></p>
						</div>
						
						<div v-for="(item, index) in rawMaterials" :key="'rm-' + index" style="position: relative;">
							<v-textarea
								v-model="rawMaterials[index]"
								outlined dense rows="1"
								placeholder="Add raw material information..."
							></v-textarea>
							<v-btn 
								v-if="rawMaterials.length > 1"
								icon x-small 
								color="error" 
								@click="removeItem('rawMaterials', index)"
								style="position: absolute; top: -8px; right: -8px; z-index: 1;"
							>
								<v-icon small>fas fa-thin fa-times</v-icon>
							</v-btn>
						</div>
						<v-btn small text color="primary" @click="addItem('rawMaterials')">
							+ Add Item
						</v-btn>
					</v-col>
					<v-col cols="6" class="pa-4">
						<div class="section-heading">End user and End of Life
							<p style="font-size: 13px; font-weight: normal;"><i>Describe the final users of your products and what happens after the product’s lifecycle.</i></p>
						</div>
						<div v-for="(item, index) in endUser" :key="'eu-' + index" style="position: relative;">
							<v-textarea
								v-model="endUser[index]"
								outlined dense rows="1"
								placeholder="Add end user/end of life information..."
							></v-textarea>
							<v-btn 
								v-if="endUser.length > 1"
								icon x-small 
								color="error" 
								@click="removeItem('endUser', index)"
								style="position: absolute; top: -8px; right: -8px; z-index: 1;"
							>
								<v-icon small>fas fa-thin fa-times</v-icon>
							</v-btn>
						</div>
						<v-btn small text color="primary" @click="addItem('endUser')">
							+ Add Item
						</v-btn>
					</v-col>
				</v-row>
			</v-card>

			<v-card class="info-section mb-3" outlined>
				<v-row no-gutters>
					<v-col cols="6" class="pa-4 border-right">
						<div class="section-heading">Factors the company impact
							<p style="font-size: 13px; font-weight: normal;"><i>Describe how your company impacts society, the environment, and the economy — both positively and negatively.</i></p>
						</div>
						
						<div class="factor-subsection">
							<div class="subsection-title">
								<span class="factor-icon positive">+</span> Positive Factors
							</div>
							<div v-for="(item, index) in factorsImpact.positive" :key="'fip-' + index" style="position: relative;">
								<v-textarea
									v-model="factorsImpact.positive[index]"
									outlined dense rows="1"
									placeholder="Add positive factor..."
								></v-textarea>
								<v-btn 
									v-if="factorsImpact.positive.length > 1"
									icon x-small 
									color="error" 
									@click="removeItem('factorsImpact', index, 'positive')"
									style="position: absolute; top: -8px; right: -8px; z-index: 1;"
								>
									<v-icon small>fas fa-thin fa-times</v-icon>
								</v-btn>
							</div>
							<v-btn small text color="success" @click="addItem('factorsImpact', 'positive')">
								+ Add Positive
							</v-btn>
						</div>

						<div class="factor-subsection mt-4">
							<div class="subsection-title">
								<span class="factor-icon negative">-</span> Negative Factors
							</div>
							<div v-for="(item, index) in factorsImpact.negative" :key="'fin-' + index" style="position: relative;">
								<v-textarea
									v-model="factorsImpact.negative[index]"
									outlined dense rows="1"
									placeholder="Add negative factor..."
								></v-textarea>
								<v-btn 
									v-if="factorsImpact.negative.length > 1"
									icon x-small 
									color="error" 
									@click="removeItem('factorsImpact', index, 'negative')"
									style="position: absolute; top: -8px; right: -8px; z-index: 1;"
								>
									<v-icon small>fas fa-thin fa-times</v-icon>
								</v-btn>
							</div>
							<v-btn small text color="error" @click="addItem('factorsImpact', 'negative')">
								+ Add Negative
							</v-btn>
						</div>
					</v-col>

					<v-col cols="6" class="pa-4">
						<div class="section-heading">Factors impacting the company
							<p style="font-size: 13px; font-weight: normal;"><i>Describe external factors that influence your company — those that help and those that may harm its performance or reputation.</i></p>
						</div>
						
						<div class="factor-subsection">
							<div class="subsection-title">
								<span class="factor-icon positive">+</span> Positive Factors
							</div>
							<div v-for="(item, index) in factorsImpacting.positive" :key="'ficp-' + index" style="position: relative;">
								<v-textarea
									v-model="factorsImpacting.positive[index]"
									outlined dense rows="1"
									placeholder="Add positive factor..."
								></v-textarea>
								<v-btn 
									v-if="factorsImpacting.positive.length > 1"
									icon x-small 
									color="error" 
									@click="removeItem('factorsImpacting', index, 'positive')"
									style="position: absolute; top: -8px; right: -8px; z-index: 1;"
								>
									<v-icon small>fas fa-thin fa-times</v-icon>
								</v-btn>
							</div>
							<v-btn small text color="success" @click="addItem('factorsImpacting', 'positive')"> + Add Positive </v-btn>
						</div>

						<div class="factor-subsection mt-4">
							<div class="subsection-title">
								<span class="factor-icon negative">-</span> Negative Factors
							</div>
							<div v-for="(item, index) in factorsImpacting.negative" :key="'ficn-' + index" style="position: relative;">
								<v-textarea
									v-model="factorsImpacting.negative[index]"
									outlined dense rows="1"
									placeholder="Add negative factor..."
								></v-textarea>
								<v-btn 
									v-if="factorsImpacting.negative.length > 1"
									icon x-small 
									color="error" 
									@click="removeItem('factorsImpacting', index, 'negative')"
									style="position: absolute; top: -8px; right: -8px; z-index: 1;"
								>
									<v-icon small>fas fa-thin fa-times</v-icon>
								</v-btn>
							</div>
							<v-btn small text color="error" @click="addItem('factorsImpacting', 'negative')">
								+ Add Negative
							</v-btn>
						</div>
					</v-col>
				</v-row>
			</v-card>

			<v-card class="info-section mb-3" outlined>
				<v-row no-gutters>
					<v-col cols="6" class="pa-4 border-right">
						<div class="section-heading">Risks
							<p style="font-size: 13px; font-weight: normal;"><i>List any risks across your value chain.</i></p>
						</div>
						<div v-for="(item, index) in risks" :key="'risk-' + index" class="mb-2" style="position: relative;">
							<v-textarea
								v-model="risks[index]"
								outlined dense rows="1"
								placeholder="Add risk information..."
							></v-textarea>
							<v-btn 
								v-if="risks.length > 1"
								icon x-small 
								color="error" 
								@click="removeItem('risks', index)"
								style="position: absolute; top: -8px; right: -8px; z-index: 1;"
							>
								<v-icon small>fas fa-thin fa-times</v-icon>
							</v-btn>
						</div>
						<v-btn small text color="primary" @click="addItem('risks')">
							+ Add Risk
						</v-btn>
					</v-col>
					<v-col cols="6" class="pa-4">
						<div class="section-heading">Opportunities
							<p style="font-size: 13px; font-weight: normal;"><i>Identify opportunities to improve or innovate sustainably.</i></p>
						</div>
						<div v-for="(item, index) in opportunities" :key="'opp-' + index" class="mb-2" style="position: relative;">
							<v-textarea
								v-model="opportunities[index]"
								outlined dense rows="1"
								placeholder="Add opportunity information..."
							></v-textarea>
							<v-btn 
								v-if="opportunities.length > 1"
								icon x-small 
								color="error" 
								@click="removeItem('opportunities', index)"
								style="position: absolute; top: -8px; right: -8px; z-index: 1;"
							>
								<v-icon small>fas fa-thin fa-times</v-icon>
							</v-btn>
						</div>
						<v-btn small text color="primary" @click="addItem('opportunities')">
							+ Add Opportunity
						</v-btn>
					</v-col>
				</v-row>
			</v-card>

			<v-card class="info-section" outlined>
				<div class="pa-4">
					<div class="section-heading">Stakeholders
						<p style="font-size: 13px; font-weight: normal;"><i>List all key internal and external stakeholders involved in your value chain.</i></p>
					</div>
					<v-row>
						<v-col 
							v-for="(item, index) in stakeholders"
							:key="'stake-' + index"
							cols="12"
							md="4"
						>
							<div style="position: relative;">
								<v-textarea
									v-model="stakeholders[index]"
									outlined dense rows="1"
									placeholder="Add stakeholder..."
								></v-textarea>
								<v-btn 
									v-if="stakeholders.length > 1"
									icon x-small
									color="error" 
									@click="removeItem('stakeholders', index)"
									style="position: absolute; top: -8px; right: -8px; z-index: 1;"
								>
									<v-icon small>fas fa-thin fa-times</v-icon>
								</v-btn>
							</div>
						</v-col>
					</v-row>
					<v-btn small text color="primary" @click="addItem('stakeholders')">
						+ Add Stakeholder
					</v-btn>
				</div>
			</v-card>

			<div class="text-center mt-6">
				<v-btn color="primary" large @click="saveValueChain">
					Save Value Chain
				</v-btn>
			</div>
		</v-card>
	</v-container>
</template>

<script>
import LoadingState from "../consts/LoadingState"
import CsrAPI from "../api/csr/csr"
import draggable from "vuedraggable"
import { mapGetters } from "vuex"

export default {
	name: "Value-Chain-View",
	components: {draggable},
	data() {
		return {
			show: false,
			isEditMode: false,
			allTiers: [],
			company: { product: "", businessModel: "" },
			rawMaterials: [""],
			endUser: [""],
			factorsImpact: { 
				positive: [""], 
				negative: [""] 
			},
			factorsImpacting: { 
				positive: [""], 
				negative: [""] 
			},
			risks: [""],
			opportunities: [""],
			stakeholders: [""],
			gotovaluechain: false
		}
	},
	created() {
		this.allTiers = [
			{ type: "supplier", tier: 3, displayName: "3 tier", placeholder: "Add supplier information", items: [""] },
			{ type: "supplier", tier: 2, displayName: "2 tier", placeholder: "Add supplier information", items: [""] },
			{ type: "supplier", tier: 1, displayName: "1 tier", placeholder: "Add supplier information", items: [""] },
			{ type: "company" },
			{ type: "customer", tier: 1, displayName: "1 tier", placeholder: "Add customer information", items: [""] },
			{ type: "customer", tier: 2, displayName: "2 tier", placeholder: "Add customer information", items: [""] }
		]
	},
	mounted() {  
		const editMode = this.$route.query.edit || this.$route.params.edit
		if (editMode) {
			this.loadExistingData()
		}
	},
	computed: {
		...mapGetters("user", ["isLoggedIn", "getUser"]),
		username() {
			return this.getUser.username
		},
	},
	methods: {
		addItem(section, subsection = null, tierIndex = null) {
			if (tierIndex !== null && (section === "suppliers" || section === "customers")) {
				this[section].tiers[tierIndex].items.push("")
			} else if (subsection && (section === "factorsImpact" || section === "factorsImpacting")) {
				this[section][subsection].push("")
			} else {
				this[section].push("")
			}
		},
		removeItem(section, index, subsection = null, tierIndex = null) {
			if (tierIndex !== null && (section === "suppliers" || section === "customers")) {
				if (this[section].tiers[tierIndex].items.length > 1) {
					this[section].tiers[tierIndex].items.splice(index, 1)
				}
			} else if (subsection && (section === "factorsImpact" || section === "factorsImpacting")) {
				if (this[section][subsection].length > 1) {
					this[section][subsection].splice(index, 1)
				}
			}else if (section === "risks" || section === "opportunities"){
				if (this[section].length > 1) {
					this[section].splice(index, 1)
				}
			} else {
				if (section === "stakeholders" && this[section].length > 1) {
					this[section].splice(index, 1)
				} else if (section !== "stakeholders" && this[section].length > 1) {
					this[section].splice(index, 1)
				}
			}
		},
		onDragEnd() {
			let supplierTiers = []
			let customerTiers = []
			let foundCompany = false
			let companyIndex = -1

			this.allTiers.forEach((tier, index) => {
				if (tier.type === "company") {
					foundCompany = true
					companyIndex = index
				} else if (!foundCompany) {
					tier.type = "supplier"
					supplierTiers.push(tier)
				} else {
					tier.type = "customer"
					customerTiers.push(tier)
				}
			})

			let supplierCount = supplierTiers.length
			supplierTiers.forEach((tier, idx) => {
				tier.tier = supplierCount - idx
				tier.displayName = `${tier.tier} tier`
				tier.placeholder = "Add supplier information"
			})

			customerTiers.forEach((tier, idx) => {
				tier.tier = idx + 1
				tier.displayName = `${tier.tier} tier`
				tier.placeholder = "Add customer information"
			})

			this.allTiers = [
				...supplierTiers,
				this.allTiers[companyIndex],
				...customerTiers
			]
		},
		addItemToTier(tierIndex) {
			this.allTiers[tierIndex].items.push("")
		},
		removeItemFromTier(tierIndex, itemIndex) {
			this.allTiers[tierIndex].items.splice(itemIndex, 1)
		},
		saveValueChain() {
			this.$store.dispatch("loading/" + LoadingState.ACTION_START_LOADING, null, { root: true })
			
			const product = this.company.product
			const businessModel = this.company.businessModel
			const suppliers = this.allTiers.filter(t => t.type === "supplier")
			const customers = this.allTiers.filter(t => t.type === "customer")

			const suppliersData = {}
			suppliers.forEach(supplier => {
				const tierKey = `tier${supplier.tier}`
				suppliersData[tierKey] = supplier.items.filter(i => i.trim())
			})

			// Build dynamic customers object
			const customersData = {}
			customers.forEach(customer => {
				const tierKey = `tier${customer.tier}`
				customersData[tierKey] = customer.items.filter(i => i.trim())
			})

			const rawMaterials = this.rawMaterials
			const endUser = this.endUser
			const positiveFactorsImpact = this.factorsImpact.positive.filter(i => i && i.trim())
			const negativeFactorsImpact = this.factorsImpact.negative.filter(i => i && i.trim())
			const positiveFactorsImpacting = this.factorsImpacting.positive.filter(i => i && i.trim())
			const negativeFactorsImpacting = this.factorsImpacting.negative.filter(i => i && i.trim())
			const risks = this.risks
			const opportunities = this.opportunities
			const stakeholders = this.stakeholders

			CsrAPI.saveValueChain(product, businessModel, suppliersData, customersData, rawMaterials, endUser, positiveFactorsImpact, negativeFactorsImpact, positiveFactorsImpacting, negativeFactorsImpacting, risks, opportunities, stakeholders)
				.then(() => {
					const message = this.isEditMode   
						? "Value chain updated successfully"   
						: "Value chain saved successfully"    
					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_LOADING_SUCCESS,
						message,
						{ root: true }
					)
					CsrAPI.getExistingValueChain()
						.then(data => {
							if (data && data.length > 0) {
								const id = data[0].ID
								this.$router.push(`/csr/esgscore/valuechain/${id}`)
							}
						})
						.catch(error => {
							console.error("Error getting ID:", error)
						})
				})
				.catch(error => {
					this.$store.dispatch("loading/" + LoadingState.ACTION_SET_ERROR, error, { root: true })
				})
		},
		loadExistingData() {
			this.$store.dispatch("loading/" + LoadingState.ACTION_START_LOADING, null, { root: true })

			CsrAPI.getExistingValueChain()
				.then(result => {
					if (result && result.length > 0) {
						this.isEditMode = true
						const data = result[0]

						this.company.product = data.Product || ""
						this.company.businessModel = data.Business_Model || ""

						const suppliers = data.Suppliers ? JSON.parse(data.Suppliers) : {}
						const customers = data.Customers ? JSON.parse(data.Customers) : {}

						const supplierTiers = Object.keys(suppliers)
							.filter(k => k.startsWith("tier"))
							.sort((a, b) => parseInt(b.replace("tier", "")) - parseInt(a.replace("tier", "")))
							.map(k => ({
								type: "supplier",
								tier: parseInt(k.replace("tier", "")),
								displayName: `${parseInt(k.replace("tier", ""))} tier`,
								placeholder: "Add supplier information",
								items: suppliers[k].length ? suppliers[k] : [""],
							}))

						const customerTiers = Object.keys(customers)
							.filter(k => k.startsWith("tier"))
							.sort((a, b) => parseInt(a.replace("tier", "")) - parseInt(b.replace("tier", "")))
							.map(k => ({
								type: "customer",
								tier: parseInt(k.replace("tier", "")),
								displayName: `${parseInt(k.replace("tier", ""))} tier`,
								placeholder: "Add customer information",
								items: customers[k].length ? customers[k] : [""],
							}))

						this.allTiers = [
							...supplierTiers,
							{ type: "company" },
							...customerTiers,
						]

						const parseArray = val => {
							if (!val) return [""]
							const arr = typeof val === "string" ? JSON.parse(val) : val
							return arr.length ? arr : [""]
						}

						this.rawMaterials = parseArray(data.Row_Materials)
						this.endUser = parseArray(data.End_Users)
						this.factorsImpact = typeof data.Factors_Impact === "string" ? JSON.parse(data.Factors_Impact) : (data.Factors_Impact || { positive: [""], negative: [""] })
						this.factorsImpacting = typeof data.Factors_Impacting === "string" ? JSON.parse(data.Factors_Impacting) : (data.Factors_Impacting || { positive: [""], negative: [""] })
						this.risks = parseArray(data.Risks)
						this.opportunities = parseArray(data.Opportunities)
						this.stakeholders = parseArray(data.Stakeholders)
					}

					this.$store.dispatch("loading/" + LoadingState.ACTION_END_LOADING, null, { root: true })
				})
				.catch(error => {
					console.error("Error loading value chain data:", error)
					this.$store.dispatch("loading/" + LoadingState.ACTION_SET_ERROR, error, { root: true })
				})
		}
	}
}
</script>


<style scoped>
.timelinetitle {
	font-size: 18px;
	font-weight: 600;
	color: #333;
}

.flex-container {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin: 20px 0;
}

.tab-title {
	display: inline-block;
	padding: 10px 30px;
	margin: 0 10px;
	font-size: 14px;
	font-weight: 600;
	color: #999;
	cursor: pointer;
}

.active-tab {
	color: #2d9b9b;
	border-bottom: 3px solid #2d9b9b;
}

.value-chain-container {
	position: relative;
	display: flex;
	align-items: center;
	margin: 30px 0;
}

.section-label {
	position: absolute;
	left: 0;
	top: -30px;
	font-weight: 600;
	font-size: 14px;
	color: #666;
	letter-spacing: 1px;
}

.section-label-right {
	position: absolute;
	right: 0;
	top: -30px;
	font-weight: 600;
	font-size: 14px;
	color: #666;
	letter-spacing: 1px;
}

.chain-boxes {
	display: flex;
	gap: 15px;
	width: 100%;
	justify-content: center;
}

.tier-box {
	flex: 0 0 calc((100% - 120px) / 6);
	min-height: 250px;
	padding: 20px;
	border: 2px solid #2d9b9b !important;
	border-radius: 20px !important;
	display: flex;
	flex-direction: column;
}

.company-box {
	color: white;
}

.tier-title {
	font-size: 16px;
	font-weight: 600;
	margin-bottom: 15px;
	text-align: center;
}

.company-title {
	font-size: 18px;
	font-weight: 600;
	margin-bottom: 15px;
	text-align: center;
}

.info-section {
	border-radius: 15px !important;
	border: 2px solid #2d9b9b !important;
}

.add-tier-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 60px;
}

.section-heading {
	font-size: 16px;
	font-weight: 600;
	margin-bottom: 15px;
	color: #333;
}

.border-right {
	border-right: 1px solid #e0e0e0;
}

.factor-subsection {
	margin-bottom: 10px;
}

.subsection-title {
	font-size: 14px;
	font-weight: 600;
	margin-bottom: 10px;
	color: #555;
	display: flex;
	align-items: center;
}

.factor-icon {
	font-size: 20px;
	font-weight: bold;
	margin-right: 10px;
	width: 25px;
}

.positive {
	color: #4caf50;
}

.negative {
	color: #f44336;
}

.square-textarea >>> .v-input__control .v-input__slot {
	border-radius: 4px !important;
}

.square-textarea >>> fieldset {
	border-radius: 4px !important;
}

@media (max-width: 1200px) {
	.chain-boxes {
		flex-wrap: wrap;
	}
	
	.tier-box {
		min-width: 45%;
	}
}
</style>